/**
 * Scheduler Service
 * 
 * Handles all scheduler (recurring payments) related API calls.
 * Premium feature only.
 * 
 * @validates Requirements 9.1-9.8 - Scheduler management
 */

import api from './api';
import { 
  Scheduler, 
  CreateSchedulerRequest, 
  UpdateSchedulerRequest,
  OperationToSchedulerRequest,
  ApiResponse,
  ExportFormat,
  SchedulerExportColumn,
} from '@budgira/shared';

export const schedulerService = {
  /**
   * Get all schedulers for an account
   */
  async list(
    accountId: string,
    status?: 'active' | 'terminated' | 'all'
  ): Promise<ApiResponse<Scheduler[]>> {
    const params = new URLSearchParams();
    if (status) {
      params.append('status', status);
    }
    const queryString = params.toString();
    const url = `/accounts/${accountId}/schedulers${queryString ? `?${queryString}` : ''}`;
    const response = await api.get<ApiResponse<Scheduler[]>>(url);
    return response.data;
  },

  /**
   * Get a specific scheduler by ID
   */
  async getById(id: string): Promise<ApiResponse<Scheduler>> {
    const response = await api.get<ApiResponse<Scheduler>>(`/schedulers/${id}`);
    return response.data;
  },

  /**
   * Create a new scheduler
   */
  async create(
    accountId: string, 
    data: CreateSchedulerRequest
  ): Promise<ApiResponse<Scheduler>> {
    const response = await api.post<ApiResponse<Scheduler>>(
      `/accounts/${accountId}/schedulers`, 
      data
    );
    return response.data;
  },

  /**
   * Update an existing scheduler
   */
  async update(
    id: string, 
    data: UpdateSchedulerRequest
  ): Promise<ApiResponse<Scheduler>> {
    const response = await api.put<ApiResponse<Scheduler>>(`/schedulers/${id}`, data);
    return response.data;
  },

  /**
   * Delete a scheduler
   */
  async delete(id: string): Promise<ApiResponse<null>> {
    const response = await api.delete<ApiResponse<null>>(`/schedulers/${id}`);
    return response.data;
  },

  /**
   * Terminate a scheduler (stop future occurrences)
   */
  async terminate(id: string): Promise<ApiResponse<Scheduler>> {
    const response = await api.post<ApiResponse<Scheduler>>(`/schedulers/${id}/terminate`);
    return response.data;
  },

  /**
   * Create a transfer scheduler between two accounts
   */
  async createTransferScheduler(data: {
    sourceAccountId: string;
    destinationAccountId: string;
    startDate: string;
    endDate?: string;
    amount: number;
    periodicity: string;
    debitCategoryId: string;
    creditCategoryId: string;
    reference?: string;
    comment?: string;
    autoPoint?: boolean;
  }): Promise<ApiResponse<{ sourceScheduler: Scheduler; destinationScheduler: Scheduler }>> {
    const response = await api.post<ApiResponse<{ sourceScheduler: Scheduler; destinationScheduler: Scheduler }>>('/schedulers/transfer', data);
    return response.data;
  },

  /**
   * Transform an existing operation into a scheduler
   */
  async createFromOperation(
    operationId: string,
    data: OperationToSchedulerRequest
  ): Promise<ApiResponse<Scheduler>> {
    const response = await api.post<ApiResponse<Scheduler>>(
      `/operations/${operationId}/to-scheduler`,
      data
    );
    return response.data;
  },

  /**
   * Generate operations from schedulers until a specified date
   */
  async generateOperations(
    accountId: string,
    untilDate: string
  ): Promise<ApiResponse<{ generatedCount: number }>> {
    const response = await api.post<ApiResponse<{ generatedCount: number }>>(
      `/accounts/${accountId}/schedulers/generate`,
      { untilDate }
    );
    return response.data;
  },

  /**
   * Export schedulers to CSV or JSON
   */
  async export(
    accountId: string,
    format: ExportFormat,
    status?: 'all' | 'active' | 'terminated',
    columns?: SchedulerExportColumn[],
    delimiter?: string,
    columnHeaders?: Record<string, string>,
    yesLabel?: string,
    noLabel?: string,
    periodicityLabels?: Record<string, string>,
    statusLabels?: Record<string, string>
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', format);
    
    // Add status filter
    if (status && status !== 'all') {
      params.append('status', status);
    }
    
    // Add columns if specified
    if (columns && columns.length > 0) {
      params.append('columns', columns.join(','));
    }
    
    // Add delimiter for CSV
    if (delimiter && format === 'csv') {
      params.append('delimiter', delimiter);
    }
    
    // Add column headers for localization
    if (columnHeaders && Object.keys(columnHeaders).length > 0) {
      params.append('columnHeaders', JSON.stringify(columnHeaders));
    }
    
    // Add yes/no labels for localization
    if (yesLabel) {
      params.append('yesLabel', yesLabel);
    }
    if (noLabel) {
      params.append('noLabel', noLabel);
    }
    
    // Add periodicity labels for localization
    if (periodicityLabels && Object.keys(periodicityLabels).length > 0) {
      params.append('periodicityLabels', JSON.stringify(periodicityLabels));
    }
    
    // Add status labels for localization
    if (statusLabels && Object.keys(statusLabels).length > 0) {
      params.append('statusLabels', JSON.stringify(statusLabels));
    }
    
    // Always use signed amounts
    params.append('signedAmount', 'true');
    
    const response = await api.get(`/accounts/${accountId}/schedulers/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default schedulerService;
